import { buildPermission } from "portal/utils/auth";
import {
  ConfigHealthCard,
  SafetyRadiusCard,
  WheelEncodersCard,
} from "portal/components/robots/RobotMachineHealth";
import { DateTime } from "luxon";
import { DEFAULT_TIMEZONE } from "portal/utils/dates";
import { formatStatus, getColor, isOffline } from "portal/utils/robots";
import { getConfigNode } from "portal/utils/configs";
import { isDevelopment, isStaging } from "portal/utils/environment";
import { LaserWeederSummaryCard } from "portal/components/laserweeder/LaserWeederSummaryCard";
import {
  PermissionAction,
  PermissionDomain,
  PermissionResource,
} from "protos/portal/auth";
import { portalApi } from "portal/state/portalApi";
import { RobotSummary } from "../../components/robots/RobotSummary";
import { RobotSummaryHeadlineInfo } from "portal/components/robots/RobotSummaryCard";
import { RobotSummaryResponse } from "protos/portal/robots";
import { RobotView } from "../robots/AnyRobotPage";
import { skipToken } from "@reduxjs/toolkit/query";
import { SupportLinkProps } from "portal/components/robots/SupportLinksCard";
import { toQuery } from "common/utils/browser";
import { useAuthorizationRequired } from "portal/components/auth/WithAuthorizationRequired";
import { useJigsawMetrics } from "portal/utils/hooks/useJigsawMetrics";
import { useParams } from "react-router-dom";
import { useQueryPopups } from "portal/utils/hooks/useApiPopups";
import { useRobotHistory } from "../robots/useRobotHistory";
import { useTranslation } from "react-i18next";
import ChipChartIcon from "@mui/icons-material/GridOn";
import ImagesIcon from "@mui/icons-material/Filter";
import PendingIcon from "@mui/icons-material/PendingOutlined";
import React, { FunctionComponent } from "react";
import StatusErrorIcon from "@mui/icons-material/ErrorOutlined";
import StatusOkIcon from "@mui/icons-material/CheckCircleOutlined";

interface LaserWeederSummaryProps {
  offerViewSwitchButton?: RobotView;
}

export function useHeadlineInfo(
  summary: RobotSummaryResponse | undefined
): RobotSummaryHeadlineInfo {
  const { t, i18n } = useTranslation();
  const robotCropDisplayName = summary?.jigsawCrop?.localizedName;

  if (summary) {
    const color = getColor(summary);
    let StatusIcon;

    const checkin = summary.robot?.health?.reportedAt
      ? DateTime.fromSeconds(summary.robot.health.reportedAt, {
          zone: "utc",
        }).setZone(
          summary.robot.health.timezone === ""
            ? DEFAULT_TIMEZONE
            : summary.robot.health.timezone
        )
      : undefined;

    if (isOffline(summary.robot)) {
      StatusIcon = PendingIcon;
    } else if (color === "error") {
      StatusIcon = StatusErrorIcon;
    } else if (color === "warning") {
      StatusIcon = PendingIcon;
    } else {
      StatusIcon = StatusOkIcon;
    }

    return {
      StatusIcon,
      statusColor: color,
      headlineText: formatStatus(t, i18n, summary.robot, robotCropDisplayName),
      checkedIn: checkin,
    };
  } else {
    return {
      StatusIcon: PendingIcon,
      statusColor: "textDisabled",
      headlineText: "",
      checkedIn: undefined,
    };
  }
}

function useVeselkaSupportLinks(
  summary: RobotSummaryResponse | undefined
): SupportLinkProps[] {
  const hasSupportLinks = useAuthorizationRequired(
    buildPermission(
      PermissionAction.read,
      PermissionResource.shortcuts_internal,
      PermissionDomain.all
    )
  );

  const healthLog = summary?.robot?.health;
  const modelId = healthLog?.model;
  const location = healthLog?.location;
  const categoryCollectionProfileId =
    healthLog?.fieldConfig?.activeCategoryCollectionId;

  const veselkaAnalyticsOrigin = (() => {
    if (isStaging) {
      return "https://veselka-analytics-stg.cloud.carbonrobotics.com";
    }
    if (isDevelopment) {
      return "https://veselka-analytics.dev.cloud.carbonrobotics.com";
    }
    return "https://veselka-analytics.cloud.carbonrobotics.com";
  })();

  return hasSupportLinks
    ? [
        {
          href: `https://veselka-analytics.cloud.carbonrobotics.com/dataset_viz?${toQuery(
            {
              model_id: modelId,
              timestamp: DateTime.now().toISO(),
              location: `${location?.x}, ${location?.y}`,
            }
          )}`,
          LinkIcon: ImagesIcon,
          i18nKey:
            "views.fleet.robots.summary.supportLinks.datasetVisualization",
        },
        {
          href: `${veselkaAnalyticsOrigin}/cross_correlation_viewer?${toQuery({
            model_id: modelId,
            category_collection_id: categoryCollectionProfileId,
          })}`,
          LinkIcon: ChipChartIcon,
          i18nKey: "views.fleet.robots.summary.supportLinks.chipChart",
        },
      ]
    : [];
}

export const LaserWeederSummary: FunctionComponent<LaserWeederSummaryProps> = ({
  offerViewSwitchButton,
}) => {
  const { i18n } = useTranslation();

  const { serial } = useParams();
  const useJigsaw = useJigsawMetrics();
  const { data: summary, isSuccess } = useQueryPopups(
    portalApi.endpoints.getRobot.useQuery(
      serial ? { language: i18n.language, serial, useJigsaw } : skipToken
    )
  );

  const history = useRobotHistory(summary?.robot?.serial);
  const { logs, hasBlocks, isLoading: isHistoryLoading } = history;

  const { data: configData } = useQueryPopups(
    portalApi.endpoints.getConfig.useQuery(serial ? { serial } : skipToken),
    {
      errorVariant: "warning",
    }
  );
  const hasMachineHealthCards = useAuthorizationRequired({
    or: [
      buildPermission(
        PermissionAction.read,
        PermissionResource.robots_health,
        PermissionDomain.customer
      ),
      buildPermission(
        PermissionAction.read,
        PermissionResource.robots_health,
        PermissionDomain.all
      ),
    ],
  });
  const headlineInfo = useHeadlineInfo(summary);
  const supportLinks = useVeselkaSupportLinks(summary);

  const config = configData ? getConfigNode(configData.config) : undefined;
  const template = configData ? getConfigNode(configData.template) : undefined;

  return (
    <RobotSummary
      success={isSuccess}
      serial={serial}
      customerSerial={summary?.robot?.customerSerial}
      headlineInfo={headlineInfo}
      mapInfo={{
        summary,
        isHistoryLoading,
        healthLogs: logs,
        blocks: history,
        hasBlocks: Boolean(hasBlocks),
      }}
      offerViewSwitchButton={offerViewSwitchButton}
      summaryCard={
        serial ? (
          <LaserWeederSummaryCard
            serial={serial}
            summary={summary}
            variant="sidebar"
          />
        ) : (
          <></>
        )
      }
      machineHealthCards={
        hasMachineHealthCards
          ? [
              <ConfigHealthCard
                className="min-w-52 flex-1"
                config={config}
                template={template}
                health={summary?.robot?.health}
                summary={summary}
                key="config"
              />,
              <WheelEncodersCard
                className="min-w-52 flex-1"
                serial={summary?.robot?.serial}
                config={config}
                key="encoders"
              />,
              <SafetyRadiusCard
                className="min-w-52 flex-1"
                serial={summary?.robot?.serial}
                config={config}
                key="safety-radius"
              />,
            ]
          : []
      }
      supportLinks={supportLinks}
    />
  );
};
